import liff from "@line/liff";
import GetFriendship from "@line/liff/get-friendship";
import RequestFriendship from "@line/liff/request-friendship";

// Both friendship APIs are opt-in modules — the default `@line/liff` bundle
// ships without them, and `liff.use()` must run before `liff.init()`.
liff.use(new GetFriendship());
liff.use(new RequestFriendship());

/**
 * Single LIFF init/login entry point. DESIGN.md §5's access-control chain
 * starts here on the client: `liff.init` -> `liff.login` (if needed) ->
 * `liff.getIDToken()`, which is then sent to the backend on every API call
 * (see api.ts) and re-verified there. The frontend never decides who can
 * see what — it only supplies proof of identity; the backend enforces
 * access via assertArchiveAccess.
 */

let initialized = false;

export async function ensureLiffReady(): Promise<void> {
  if (initialized) return;
  await liff.init({ liffId: import.meta.env.VITE_LIFF_ID });
  if (!liff.isLoggedIn()) {
    // Explicit redirectUri keeps the current query string (the ?gid=... that
    // selects a group archive, see getGroupIdFromUrl) across the login
    // round-trip — the default redirect target is the LIFF endpoint URL
    // without our params.
    liff.login({ redirectUri: window.location.href });
    // liff.login() redirects away; execution effectively stops here.
    return;
  }
  initialized = true;
}

export function getIdToken(): string {
  const token = liff.getIDToken();
  if (!token) throw new Error("BarkUp liff: no ID token available — user is not logged in");
  return token;
}

/**
 * The LINE groupId whose archive this session should show, or null for the
 * caller's own 1:1 archive. Used purely to decide WHICH archive to ask the
 * backend to resolve — never used as an access-control decision by itself,
 * see archive.ts resolve route's independent membership re-check.
 *
 * Comes from the `?gid=` query param the bot embeds in every group-facing
 * LIFF link (line/flex/messages.ts). Do NOT reach for `liff.getContext()`
 * here: LINE discontinued its groupId on 2023-02-06 and it now yields a
 * random per-launch identifier that matches nothing — resolving with it
 * 404s every time.
 *
 * Checked in two places because of how liff.init handles deep links: after
 * init the param normally lands in location.search directly, but on the
 * first hop LINE passes the whole original query encoded inside a
 * `liff.state` param instead.
 */
export function getGroupIdFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const direct = params.get("gid");
  if (direct) return direct;

  const liffState = params.get("liff.state");
  if (liffState) {
    const idx = liffState.indexOf("?");
    if (idx !== -1) {
      const nested = new URLSearchParams(liffState.slice(idx));
      const fromState = nested.get("gid");
      if (fromState) return fromState;
    }
  }
  return null;
}

/**
 * The screen the LIFF app should open on, from the `?screen=` param the rich
 * menu embeds in its deep links (apps/api line/richMenu.ts). Returns null for a
 * normal launch, which lands on the default archive screen.
 *
 * Needed because the app uses hash-history routing: a path after the liffId
 * (`liff.line.me/<id>/settings`) never reaches the hash router, so the rich
 * menu can't select a route that way — it passes `?screen=settings` instead and
 * main.ts navigates there on boot. Checked in both location.search and the
 * `liff.state` wrapper for the same first-hop reason as getGroupIdFromUrl.
 */
export function getInitialScreenFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const direct = params.get("screen");
  if (direct) return direct;

  const liffState = params.get("liff.state");
  if (liffState) {
    const idx = liffState.indexOf("?");
    if (idx !== -1) {
      const nested = new URLSearchParams(liffState.slice(idx));
      const fromState = nested.get("screen");
      if (fromState) return fromState;
    }
  }
  return null;
}

/**
 * Whether the caller has the BarkUp Official Account as a friend.
 *
 * This gates the whole app (main.ts): BarkUp is a bot that saves what you send
 * it and pushes back links/notifications, none of which the Messaging API can
 * do to a non-friend — so a stranger who opened the LIFF app would land in an
 * archive that can never fill up or reach them.
 *
 * Deliberately fails OPEN: if LINE's friendship endpoint errors (or we're in a
 * plain dev browser where it isn't callable), locking every user out of their
 * own archive is a far worse failure than letting a non-friend slip through —
 * the bot side still can't message them, so nothing is actually bypassed.
 */
export async function isOaFriend(): Promise<boolean> {
  try {
    const { friendFlag } = await liff.getFriendship();
    return friendFlag;
  } catch {
    return true;
  }
}

/**
 * Opens LINE's native "add this Official Account" subwindow (or the unblock
 * prompt, if the OA was blocked).
 *
 * Only available inside the LINE client AND only when the LIFF app's screen
 * size is **Full** — it rejects otherwise, so callers must treat a throw as
 * "couldn't ask", not "user declined". It also resolves as soon as the
 * subwindow closes without reporting what the user chose: the only way to know
 * is to re-check isOaFriend() afterwards.
 */
export async function promptAddFriend(): Promise<void> {
  await liff.requestFriendship();
}

/** Header avatar only — never used for access decisions (those stay on the ID token, see api.ts). */
export async function getViewerProfile(): Promise<{ displayName: string; pictureUrl: string | null } | null> {
  try {
    const profile = await liff.getProfile();
    return { displayName: profile.displayName, pictureUrl: profile.pictureUrl ?? null };
  } catch {
    return null;
  }
}

/**
 * Closes the LIFF window — used as the terminal action after account
 * deletion (DESIGN.md §7.1): there's nowhere sensible left to navigate to
 * inside the app once the caller's own archive may be gone, so ending the
 * session is the calmest option. Only works when actually opened as a full
 * LIFF app inside LINE; a no-op elsewhere (dev in a plain browser tab) is
 * fine — the user just closes the tab themselves.
 */
export function closeLiffWindow(): void {
  try {
    liff.closeWindow();
  } catch {
    // Not fatal — see doc comment above.
  }
}

/**
 * Opens a URL in the device's real browser rather than LINE's in-app webview.
 *
 * Required for Stripe Checkout: 3-D Secure card challenges and the PromptPay
 * hand-off to a banking app both depend on browser features and app-switching
 * that the in-app webview handles inconsistently — a payment that silently
 * dead-ends there looks like the app is broken, and the user has already
 * decided to pay by that point.
 *
 * `external: true` is the part that matters; falling back to window.open keeps
 * this working in a plain browser during dev, where liff.openWindow throws
 * because there is no LINE client to hand off to.
 */
export function openExternal(url: string): void {
  try {
    liff.openWindow({ url, external: true });
  } catch {
    window.open(url, "_blank", "noopener");
  }
}
